import { LOGIN_REDIRECT_STORAGE_KEY } from '../storage'

/**
 * The sign-in app. Same origin, but a separate application served by the proxy —
 * not a route of this shell — so reaching it is a full-page navigation, the same
 * reason the profile pilet's header action is a plain `<a href='/login'>`.
 */
export const LOGIN_PATH = '/login'

/**
 * Whether we are already sitting on the sign-in app. Prefix-matched rather than
 * compared, so `/login/`, `/login?next=…` and any sub-path the sign-in flow walks
 * through all count as "already there".
 */
const isOnLoginPath = (pathname: string) =>
  pathname === LOGIN_PATH || pathname.startsWith(`${LOGIN_PATH}/`) || pathname.startsWith(`${LOGIN_PATH}?`)

/**
 * Send an anonymous visitor of a private library to the sign-in app, at most once
 * per tab. Returns whether the navigation was actually started — the caller keeps
 * the loading screen up when it was, so the app never flashes behind a page that is
 * already leaving.
 *
 * **The loop is the whole difficulty here.** The shell is served by a catch-all:
 * an unknown path returns `index.html` (the pull zone's custom 404 page, which is
 * what makes deep links work — see the *Releasing* section of CLAUDE.md). So if
 * `/login` is ever not routed to the sign-in app — a proxy misconfiguration, a
 * local dev server without that route — it boots *this shell* instead, which reads
 * the same private configuration, sees the same anonymous visitor, and navigates to
 * `/login` again. That is a hard reload loop, and the visitor cannot break out of it
 * because every iteration starts before anything is rendered.
 *
 * Two independent guards, because they fail differently:
 *
 * 1. **The path check** catches the common shape — the shell booting *at* `/login`.
 *    Stateless, so it works on the very first load and cannot be defeated by storage
 *    being unavailable.
 * 2. **The once-per-tab marker** catches everything else — a sign-in app that lands
 *    on some *other* path and bounces back, or any redirect chain the path check
 *    cannot see the shape of. `sessionStorage`, not `localStorage`: the guard should
 *    last exactly as long as the navigation sequence it is protecting, and a stale
 *    flag in a new tab would silently stop a legitimate redirect.
 *
 * When the redirect is suppressed the shell simply renders as it would for any
 * anonymous visitor. On a private domain that means empty rows — a poor page, but a
 * reachable one, which is the right side to fail on.
 */
export function redirectToLoginOnce (): boolean {
  if (isOnLoginPath(window.location.pathname)) {
    console.warn('[auth] private domain, but already on the sign-in path — not redirecting')
    return false
  }
  try {
    if (window.sessionStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY)) {
      console.warn('[auth] private domain, but a sign-in redirect already happened in this tab — not redirecting again')
      return false
    }
    window.sessionStorage.setItem(LOGIN_REDIRECT_STORAGE_KEY, '1')
  } catch {
    // Storage blocked (private mode, embedded webview). The path check above is
    // still in force, so proceed — a single redirect with one guard beats leaving
    // an anonymous visitor on an empty private library.
  }
  // `assign`, not `replace`: Back must return to wherever the visitor came from
  // rather than skipping past a page that only ever redirected.
  window.location.assign(LOGIN_PATH)
  return true
}

/**
 * Drop the once-per-tab marker. Called whenever the shell starts normally, so the
 * guard only ever suppresses a *consecutive* redirect: a visitor who signs in, comes
 * back, and later loses the session in the same tab gets sent to sign in again.
 */
export function clearLoginRedirectMark (): void {
  try {
    window.sessionStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
